import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import { useCustomer } from "../context/CustomerContext";
import { formatDateTime } from "../utils/datetime";

type AIAction = {
  id: number;
  device_id: number | null;
  device_hostname: string | null;
  customer_name: string | null;
  customer_id: number | null;
  ticket_id: number | null;
  alert_id: number | null;
  action_type: string;
  params: Record<string, any>;
  risk_tier: "low" | "medium" | "high";
  status: string;
  proposed_reason: string | null;
  instruction?: string | null;
  consequence?: string | null;
  disruptive?: boolean;
  provider_used: string | null;
  model_used: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  executed_at: string | null;
  result: Record<string, any> | null;
  error_message: string | null;
  created_at: string;
};

const RISK_COLORS: Record<string, string> = {
  low: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
  medium: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  high: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
};

const STATUS_COLORS: Record<string, string> = {
  proposed: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  approved: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
  executing: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300",
  succeeded: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  failed: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  rejected: "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
  cancelled: "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
};

export default function AIActions() {
  const { currentCustomerId, current } = useCustomer();
  const [actions, setActions] = useState<AIAction[]>([]);
  const [filter, setFilter] = useState("proposed");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const params: Record<string, unknown> = { limit: 200 };
      if (filter !== "all") params.status = filter;
      // Scope to the globally-selected customer (null = All customers).
      if (currentCustomerId != null) params.customer_id = currentCustomerId;
      const r = await api.get("/api/v1/ai/actions", { params });
      setActions(r.data);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Load failed");
    } finally {
      setBusy(false);
    }
  }, [filter, currentCustomerId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const approve = async (a: AIAction) => {
    const needNote = a.risk_tier === "high";
    const note = needNote
      ? window.prompt(
          "HIGH-risk action — enter approval note (required):",
          ""
        )
      : window.prompt("Approval note (optional):", "");
    if (needNote && !note) return;
    try {
      await api.post(`/api/v1/ai/actions/${a.id}/approve`, { note: note || undefined });
      await refresh();
    } catch (e: any) {
      alert("Approve failed: " + (e?.response?.data?.detail || e?.message));
    }
  };

  const reject = async (a: AIAction) => {
    const note = window.prompt("Reason for rejection (optional):", "");
    if (note === null) return;
    try {
      await api.post(`/api/v1/ai/actions/${a.id}/reject`, { note: note || undefined });
      await refresh();
    } catch (e: any) {
      alert("Reject failed: " + (e?.response?.data?.detail || e?.message));
    }
  };

  const execute = async (a: AIAction) => {
    if (!window.confirm(`Execute action #${a.id} (${a.action_type})?`)) return;
    try {
      await api.post(`/api/v1/ai/actions/${a.id}/execute`, {});
      await refresh();
    } catch (e: any) {
      alert("Execute failed: " + (e?.response?.data?.detail || e?.message));
    }
  };

  const approveAndExecute = async (a: AIAction) => {
    const needNote = a.risk_tier === "high";
    const note = needNote
      ? window.prompt(
          "HIGH-risk action — enter approval note (required):",
          ""
        )
      : window.prompt("Approval note (optional):", "");
    if (needNote && !note) return;
    if (!window.confirm(`Approve AND execute action #${a.id} (${a.action_type})?`)) return;
    try {
      await api.post(`/api/v1/ai/actions/${a.id}/approve`, { note: note || undefined });
      await api.post(`/api/v1/ai/actions/${a.id}/execute`, {});
      await refresh();
    } catch (e: any) {
      alert("Failed: " + (e?.response?.data?.detail || e?.message));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">AI Actions</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Approval queue for AI-proposed changes across devices, network gear, and Microsoft 365.
            Low-risk auto-executes; medium needs one click; high needs an explicit approval note.
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Scope:{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {current ? current.name : "All customers"}
            </span>{" "}
            — change it with the customer switcher in the header.
          </p>
        </div>
        <div className="flex gap-2 text-sm">
          {["proposed", "approved", "succeeded", "failed", "rejected", "all"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1 rounded ${
                filter === s
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      {busy && <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>}

      {!busy && actions.length === 0 && (
        <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-12 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
          No actions in this state.
        </div>
      )}

      <div className="space-y-3">
        {actions.map((a) => (
          <div key={a.id} className="bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-gray-500 dark:text-gray-400">#{a.id}</span>
                  <span className="font-semibold">{a.action_type}</span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full uppercase font-bold ${
                      RISK_COLORS[a.risk_tier] || "bg-gray-100 dark:bg-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {a.risk_tier}
                  </span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      STATUS_COLORS[a.status] || "bg-gray-100 dark:bg-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {a.status}
                  </span>
                  {a.provider_used && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                      via {a.provider_used} ({a.model_used})
                    </span>
                  )}
                </div>

                {/* The consequence before the rationale: what it does to you
                    matters more than why the model likes it. */}
                {a.consequence && a.status === "proposed" && (
                  <div
                    className={`mt-2 rounded-lg border px-3 py-2 text-sm flex gap-2 ${
                      a.disruptive
                        ? "border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/25 text-amber-900 dark:text-amber-200"
                        : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {a.disruptive && <span className="shrink-0">&#9888;</span>}
                    <span>
                      {a.disruptive && <strong>This may break something. </strong>}
                      {a.consequence}
                    </span>
                  </div>
                )}
                {a.instruction && (
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                    Asked for: &ldquo;{a.instruction}&rdquo;
                  </div>
                )}
                {a.proposed_reason && (
                  <div className="text-sm text-gray-700 dark:text-gray-300 mt-2 italic">"{a.proposed_reason}"</div>
                )}

                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-mono break-all">
                  params: {JSON.stringify(a.params)}
                </div>

                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex gap-3 flex-wrap">
                  {a.device_id ? (
                    <Link
                      to={`/devices/${a.device_id}`}
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {a.customer_name ? `${a.customer_name} · ` : ""}
                      {a.device_hostname || `Device #${a.device_id}`}
                    </Link>
                  ) : a.customer_id && a.customer_name ? (
                    <Link
                      to={`/customers/${a.customer_id}`}
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {a.customer_name}
                    </Link>
                  ) : null}
                  {a.ticket_id && (
                    <Link
                      to={`/tickets/${a.ticket_id}`}
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Ticket #{a.ticket_id}
                    </Link>
                  )}
                  <span>Proposed {formatDateTime(a.created_at)}</span>
                  {a.executed_at && (
                    <span>Executed {formatDateTime(a.executed_at)}</span>
                  )}
                </div>

                {a.error_message && (
                  <div className="mt-2 text-xs bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 px-2 py-1 rounded">
                    {a.error_message}
                  </div>
                )}

                {a.result && Object.keys(a.result).length > 0 && (
                  <div className="mt-2 text-xs bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-2 py-1 rounded font-mono break-all">
                    {JSON.stringify(a.result)}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-1.5 shrink-0">
                {a.status === "proposed" && (
                  <>
                    <button
                      onClick={() => approveAndExecute(a)}
                      className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium"
                    >
                      Approve + Execute
                    </button>
                    <button
                      onClick={() => approve(a)}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs"
                    >
                      Approve only
                    </button>
                    <button
                      onClick={() => reject(a)}
                      className="px-3 py-1 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded text-xs"
                    >
                      Reject
                    </button>
                  </>
                )}
                {a.status === "approved" && (
                  <button
                    onClick={() => execute(a)}
                    className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium"
                  >
                    Execute
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
